import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, X, User as UserIcon } from "@phosphor-icons/react";

/**
 * ContactPicker — Iter 85.2 · Phase 3
 *
 * Multi-select of a client's contacts, driven by the array already on
 * `client.stakeholders`. Renders as a pill row with a "+ Contact" button
 * that opens a searchable popover. No network calls — the parent passes
 * `contacts` and `value` (list of ids), and receives the new list via
 * `onChange`. Empty state shows a subtle hint instead of a picker.
 *
 * @param {Array} contacts — the client's `stakeholders[]` array.
 * @param {Array<string>} value — selected contact_ids.
 * @param {Function} onChange — receives the new list of ids.
 * @param {string} testidPrefix — e.g. "note-contact" or "task-contact".
 */
export default function ContactPicker({ contacts = [], value = [], onChange, testidPrefix = "contact-picker" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const byId = useMemo(
    () => Object.fromEntries((contacts || []).filter((c) => c && c.id).map((c) => [c.id, c])),
    [contacts],
  );
  const selected = (value || []).filter((id) => byId[id]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (contacts || []).filter((c) => {
      if (!c?.id) return false;
      if (!q) return true;
      const hay = `${c.name || ""} ${c.title || ""} ${c.email || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [contacts, query]);

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }
  };
  const remove = (id) => onChange(selected.filter((x) => x !== id));

  if ((contacts || []).length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-testid={`${testidPrefix}-empty`}>
        No contacts on this client yet — add one under the Contacts tab first.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid={testidPrefix}>
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.map((id) => {
          const c = byId[id];
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 border border-primary/40 bg-primary/10 text-primary px-2 py-0.5 text-xs"
              data-testid={`${testidPrefix}-chip-${id}`}
            >
              <UserIcon size={10} weight="fill" />
              <span className="max-w-[10rem] truncate">{c.name}</span>
              <button
                type="button"
                onClick={() => remove(id)}
                className="ml-0.5 hover:text-destructive"
                aria-label={`Remove ${c.name}`}
                data-testid={`${testidPrefix}-chip-remove-${id}`}
              >
                <X size={10} weight="bold" />
              </button>
            </span>
          );
        })}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-none h-6 px-2 text-[0.625rem] font-mono uppercase tracking-widest"
              data-testid={`${testidPrefix}-open`}
            >
              + Contact
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="rounded-none p-0 w-72">
            <div className="p-2 border-b border-border">
              <Input
                autoFocus
                placeholder="Search…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="rounded-none h-8 text-sm"
                data-testid={`${testidPrefix}-search`}
              />
            </div>
            <ul className="max-h-64 overflow-y-auto" data-testid={`${testidPrefix}-list`}>
              {filtered.length === 0 && (
                <li className="px-3 py-4 text-xs text-muted-foreground text-center">No match.</li>
              )}
              {filtered.map((c) => {
                const active = selected.includes(c.id);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => toggle(c.id)}
                      className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-secondary/40 focus-visible:outline-none focus-visible:bg-secondary/40 ${active ? "bg-secondary/30" : ""}`}
                      data-testid={`${testidPrefix}-option-${c.id}`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium truncate">{c.name}</span>
                        {c.title && (
                          <span className="block text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground truncate">
                            {c.title}
                          </span>
                        )}
                      </span>
                      {active && <Check size={12} weight="bold" className="text-primary shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
